// ESPELHO de backend/src/services/videoEdit/contract.ts — mude os dois juntos.

export interface Region { start: number; end: number }
export interface EdlRange { source: string; start: number; end: number; beat: string; quote?: string; reason?: string; gain_db?: number; /** 1 = normal, até 2 */ speed?: number }
export interface JcutTimelineEntry { beat: string; source: string; video_start_in_output: number; video_duration: number; audio_start_in_output: number; audio_duration: number; tail_trim_frames: number }
export interface Edl { version: 1; sources: Record<string, string>; grade: string; voice_master: boolean; jcut: { lead_frames: number; tail_trim_frames: number } | false; ranges: EdlRange[]; total_duration_s: number; jcut_timeline?: JcutTimelineEntry[]; fps?: number }

// ─── Estilo (portão entre Fase 1 e Fase 2) — mesmo vocabulário do STYLE_CATALOG do edvid ───
export type EditStyleId = 'limpa' | 'split' | 'split2';
export type HeadlineStyleId = 'outline' | 'card' | 'realce' | 'misto' | 'empilhado' | 'pergunta' | 'none';
export type CaptionStyleId = 'karaoke' | 'stacked' | 'scatter' | 'simples' | 'serifada' | 'classica' | 'none';

export interface StyleElements {
  tracking: boolean; // enquadramento segue o rosto
  zoomAuto: boolean; // push-in +0.04 por segmento
  zoomCuts: boolean; // zoom seco por corte (ciclo 1.10–1.22)
  flashCut: boolean; // flash + click nas trocas de layout (split)
  music: boolean; // trilha do catálogo mixada no final
}

export interface StyleSetup {
  /** legado: ignorado quando imageCount existe (a IA decide o layout por imagem) */
  edit: EditStyleId;
  headline: HeadlineStyleId;
  captions: CaptionStyleId;
  /** hex; só realce, misto e stacked pintam */
  accent: string;
  elements: StyleElements;
  /** trilha escolhida quando elements.music */
  musicTrackId?: string | null;
  /** texto livre do usuário — sobrescreve os padrões (vai para o planner da Fase 2) */
  note?: string;
  /** hook escrito pelo usuário; vazio = a IA escreve */
  hookText?: string;
  /** headline 'empilhado': as 3 linhas (vazia = a IA preenche) */
  hookParts?: string[];
  /** headline 'pergunta': título e pergunta da caixinha (vazio = a IA preenche) */
  hookQuestion?: { title?: string; text?: string };
  /** quantidade exata de arquivos de imagem (0–8); pares contam como duas imagens */
  imageCount?: number;
  /** quantas animações da biblioteca a IA pode colocar (0–6); padrão 2 */
  graphicsCount?: number;
}

export const DEFAULT_STYLE: StyleSetup = {
  edit: 'limpa',
  headline: 'outline',
  captions: 'karaoke',
  accent: '#ff5200',
  elements: { tracking: false, zoomAuto: true, zoomCuts: true, flashCut: false, music: false },
};

// ─── Dados da Fase 2 — formatos do edvid (assets/shortform) ───
export interface CaptionWord {
  text: string;
  startMs: number;
  endMs: number;
  timestampMs: number;
  confidence: null;
}

/** caption-cues.json do edvid (só stacked) */
export interface CaptionCue {
  i: number;
  startMs: number;
  endMs: number;
  preset: 'STACK_MIXED' | 'SOLO_BIG' | 'SOLO_OUTLINE';
  exit: 'blur_up' | 'abrupt';
  styleOffset: number;
  lineStyles: number[];
  lineBoost: boolean[];
  lineEmph: boolean[];
  lines: Array<Array<{ text: string; fromMs: number; toMs: number }>>;
}

export interface SegmentsData {
  segments: Array<{ start: number; dur: number }>;
}

export interface TrackData {
  fps: number;
  width: number;
  height: number;
  count: number;
  points: Array<[number, number]>;
}

// ─── Atrás da pessoa + animações ───
// matte = ProRes 4444 (render); mattePreview = webm VP9 com alfa (o Player do navegador não decodifica ProRes)
export interface BehindImage { kind: 'image'; src: string; matte: string; mattePreview?: string; start: number; dur: number }
export interface BehindWords { kind: 'words'; words: Array<{ t: string; at: number }>; matte: string; mattePreview?: string; start: number; dur: number }
export type BehindItem = BehindImage | BehindWords;

export type MotionGraphicTemplate =
  | 'counter' // número que conta até o valor ("3x", "R$ 2.000", "87%")
  | 'checklist' // itens que entram marcando ✓
  | 'typewriter' // frase digitada com cursor
  | 'timeline' // etapas numa linha com marcador andando
  | 'arrow_callout' // seta + rótulo apontando uma região
  | 'compare_bars' // duas barras (antes × depois / nós × eles)
  | 'keyword_pop' // palavra-chave grande que salta
  | 'steps'; // passos numerados 1-2-3
export interface MotionGraphic {
  id: string;
  template: MotionGraphicTemplate;
  start: number;
  end: number;
  /** zona do quadro: o hook é dono do topo até hook.endSec; legenda no terço de baixo */
  zone: 'top' | 'middle';
  /** parâmetros do template (textos, números, cor) — ver video-compositions/graphics */
  props: Record<string, unknown>;
}


/** edit-data.json do edvid (README do shortform), com as URLs absolutas no lugar de public/ */
export interface EditData {
  width: number;
  height: number;
  fps: number;
  durationSec: number;
  camera: { enabled: boolean; zooms: number[]; pushIn: number; targetX: number; targetY: number; tracking?: boolean };
  hook: {
    enabled: boolean;
    endSec: number;
    text?: string;
    lines: string[];
    style?: Exclude<HeadlineStyleId, 'none'>;
    accent?: string;
    /** 'empilhado': 3 linhas — [forte itálico, fino, serifada de destaque] */
    parts?: string[];
    /** 'pergunta': caixinha de perguntas (título na tarja escura + pergunta no cartão claro) */
    question?: { title: string; text: string };
    logo: string | null;
    sign: string | null;
    fontSizePx?: number;
    maxFontPx?: number;
    safeWidth?: number;
    strokePx?: number;
    paddingTop?: number;
    lineHeight?: number;
  };
  captions: {
    enabled: boolean;
    style?: Exclude<CaptionStyleId, 'none'>;
    accent?: string;
    fontSize: number;
    maxWords: number;
    safeWidth: number;
    paddingBottom: number;
    windows?: Array<{ start: number; end: number; paddingBottom: number }>;
    stackedOffsetY?: number;
    fontScale?: number;
    sfx?: { enabled: boolean; clickVolume?: number; scratchVolume?: number };
    scatterOffsetY?: number;
    scatterFontSize?: number;
    scatterSafeWidth?: number;
  };
  inserts: Array<{ src: string; start: number; end: number }>;
  /** "atrás da pessoa" (edvid BehindSubject): elemento entre o fundo e a pessoa recortada (matte com alfa) */
  behind: BehindItem[];
  /** animações da biblioteca (substituem o CustomGraphics escrito à mão no edvid) */
  graphics?: MotionGraphic[];
  splitInserts?: Array<{ src: string; start: number; end: number; fit?: 'cover' | 'contain'; bandH?: number; layout?: 'top' | 'bottom'; group?: string; slot?: 'left' | 'right' }>;
  imagePlanVersion?: number;
  transitions?: Array<{ at: number; intensity?: number; sfx?: string; volume?: number }>;
  soundtrack: { enabled: boolean; file: string; volume: number };
  /**
   * Quadros que o OffthreadVideo desenha atrasado numa borda de corte. O edvid
   * assumia 1 ("verifique por projeto"); medido no Remotion 4.0.482: 0 — o quadro
   * N da composição mostra o quadro N da fonte. Layout (zoom, split, flash,
   * janelas de legenda) e o atraso do áudio na entrega seguem este valor.
   */
  videoLag?: number;
}

/** inputProps da composição Remotion "Reels" — o MESMO objeto no render-worker e no @remotion/player. */
export interface ReelsProps {
  videoSrc: string;
  /** base das URLs de SFX/fontes da composição (render-worker serve os estáticos) */
  assetBase?: string;
  editData: EditData;
  captions: CaptionWord[];
  captionCues: CaptionCue[];
  segments: SegmentsData;
  track: TrackData | null;
}

// ─── Estado do job (MarketingCreativeJob.output.edvid) ───
export type EdvidPhase =
  | 'cutting' // Fase 1 rodando
  | 'cut_ready' // cut.mp4 pronto, aguardando aprovação/estilo
  | 'visual_rendering' // Fase 2 rodando
  | 'done' // final.mp4 pronto
  | 'failed';

export interface EdvidNote {
  id: string;
  start: number;
  end: number;
  /** posição no MP4 que o usuário estava vendo */
  renderedStart?: number;
  renderedEnd?: number;
  phase: 'cut' | 'visual';
  text: string;
  status: 'open' | 'applied' | 'dismissed';
  createdAt: string;
  /** o que a IA fez com a nota (ou por que não fez) */
  result?: string;
}

export interface EdvidCutState {
  revision: number;
  url: string;
  durationSec: number;
  fps: number;
  /** EDL sem caminhos locais (sources = nome → nome original do upload) */
  edl: Omit<Edl, 'sources'> & { sources: Record<string, string> };
  packed: string;
  strategy: string;
  verifySummary: string;
  verifyFlags: number;
  warnings: string[];
  speechRegions: Record<string, Region[]>;
  sourceDurations: Record<string, number>;
  /** URL do proxy de cada fonte (a aba Corte mostra o take inteiro ao estender o trim) */
  sourcePreviewUrls: Record<string, string>;
  filmstripUrl?: string;
  waveformUrl?: string;
  approvedAt?: string | null;
}

export interface EdvidVisualState {
  revision: number;
  /** outras opções de headline geradas junto (a aba Final oferece troca com 1 clique) */
  hookOptions?: string[];
  style: StyleSetup;
  props: Omit<ReelsProps, 'videoSrc'>;
  finalUrl?: string;
  warnings: string[];
  renderedAt?: string;
}

export interface EdvidJobState {
  engine: 'edvid';
  phase: EdvidPhase;
  message?: string;
  error?: string | null;
  cut?: EdvidCutState;
  visual?: EdvidVisualState;
  /** estilo escolhido antes da Fase 2 terminar (ou atalho "usar padrão da marca") */
  pendingStyle?: StyleSetup | null;
  /** identidade da marca do job: a aba Estilo já abre com a cor da marca selecionada */
  brand?: { accent?: string | null; name?: string | null; logoUrl?: string | null };
  /** opções de headline geradas na aba Estilo (por revisão do corte) — reaproveitadas na Fase 2 */
  hookOptionsCache?: { revision: number; options: string[] };
  notes: EdvidNote[];
}

// ─── API (prefixo /marketing-studio/video-editor) ───
//   POST /jobs                          options.engine='edvid' (padrão com VIDEO_EDITOR_ENGINE=edvid) — só direção/duração
//   GET  /jobs/:id                      → job com output.edvid: EdvidJobState
//   PUT  /jobs/:id/cut                  { ranges: EdlRange[], baseRevision } → re-render Fase 1 (edição da aba Corte)
//   POST /jobs/:id/recut                { note? } → refaz o plano de corte pela IA
//   POST /jobs/:id/style                { style: StyleSetup } → aprova o corte e dispara a Fase 2
//   PUT  /jobs/:id/visual               { props: EdvidVisualState['props'], baseRevision } → re-render final
//   POST /jobs/:id/notes                { notes: EdvidNote[] } → IA aplica as marcações abertas
//   POST /jobs/:id/hooks                { } → { options: string[] } opções de headline (aba Estilo, antes do render; ~5s)
